import React from 'react';

import { MOBILE_NAV } from '../../../../theme/tokens';
import { Glyph } from './SparkleGlyph.styles';

/**
 * Lumio sparkle — the 4-point spark in the mobile nav's center orb (Figma node
 * 5997:23033), replacing the fetched `lumio-icon` asset. Drawn inline (not via
 * `<img>`) so its pink→cyan gradient and cyan inner glow render from theme
 * tokens rather than a remote SVG.
 *
 * The inner glow is kept CENTRED (no directional offset): Figma's export shoved
 * the alpha left (`dx`), which piled the cyan glow onto the spark's right edge
 * and read as lopsided/offset — especially on light theme, where the dark orb is
 * a high-contrast focal point. A symmetric, subtler glow keeps the life without
 * the skew.
 */
export type SparkleGlyphProps = {
  size?: number;
  className?: string;
};

/**
 * Hex → an feColorMatrix `values` string that tints SourceAlpha to that colour.
 * Keeps the inner-shadow glow as a single token (no raw colour literal in the
 * matrix). Mirrors Figma's export: rows = [0 0 0 0 R / G / B], alpha row passthrough.
 */
const toColorMatrix = (hex: string): string => {
  const n = parseInt(hex.slice(1), 16);
  const r = ((n >> 16) & 255) / 255;
  const g = ((n >> 8) & 255) / 255;
  const b = (n & 255) / 255;
  return `0 0 0 0 ${r} 0 0 0 0 ${g} 0 0 0 0 ${b} 0 0 0 1 0`;
};

export const SparkleGlyph = ({ size = 26, className }: SparkleGlyphProps): React.ReactElement => (
  <Glyph className={className} aria-hidden="true">
    <svg width={size} height={size} viewBox="0 0 26 26" fill="none">
      <g filter="url(#sparkle-inner-shadow)">
        <path
          d="M10.2365 1.97578C11.1094 -0.578808 14.6391 -0.656176 15.6741 1.74367L15.7617 1.97724L16.9397 5.42227C17.2097 6.21235 17.646 6.93533 18.2191 7.54247C18.7923 8.1496 19.489 8.62676 20.2622 8.94176L20.5789 9.06L24.024 10.2366C26.5786 11.1095 26.656 14.6392 24.2576 15.6742L24.024 15.7618L20.5789 16.9398C19.7886 17.2096 19.0653 17.6458 18.4579 18.2189C17.8505 18.7921 17.3731 19.4888 17.058 20.2622L16.9397 20.5775L15.7632 24.024C14.8902 26.5786 11.3605 26.656 10.327 24.2576L10.2365 24.024L9.05989 20.579C8.7901 19.7886 8.35391 19.0654 7.78076 18.458C7.2076 17.8506 6.51083 17.3732 5.73745 17.058L5.42214 16.9398L1.97708 15.7632C-0.578978 14.8903 -0.656346 11.3606 1.74352 10.3271L1.97708 10.2366L5.42214 9.06C6.21222 8.79003 6.93521 8.35376 7.54235 7.78062C8.14949 7.20748 8.62665 6.51081 8.94165 5.73758L9.05989 5.42227L10.2365 1.97578Z"
          fill="url(#sparkle-gradient)"
          fillOpacity="0.92"
        />
      </g>
      <defs>
        <filter
          id="sparkle-inner-shadow"
          x="-2"
          y="-2"
          width="30"
          height="30"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          {/* No feOffset: a centred inner glow (even cyan rim) instead of the
              skewed-right shadow Figma exported. */}
          <feGaussianBlur stdDeviation="0.7" />
          <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
          <feColorMatrix type="matrix" values={toColorMatrix(MOBILE_NAV.sparkleGlow)} />
          <feBlend mode="normal" in2="shape" result="effect1_innerShadow" />
        </filter>
        <linearGradient
          id="sparkle-gradient"
          x1="5.29398"
          y1="4.60482"
          x2="26"
          y2="13"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={MOBILE_NAV.sparkleStart} />
          <stop offset="1" stopColor={MOBILE_NAV.sparkleEnd} />
        </linearGradient>
      </defs>
    </svg>
  </Glyph>
);

export default SparkleGlyph;
